import React, { useState, useEffect, useRef } from "react";
import { collection, addDoc, getFirestore, Timestamp, doc, getDocs, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import FavouriteButton from "./FavouriteButton";

const BARCODE_LENGTH = 13;
const LOCATION_MAX_LENGTH = 15;

const sanitizeBarcode = (value) => value.replace(/\D/g, "").slice(0, BARCODE_LENGTH);
const limitLocation = (value) => value.slice(0, LOCATION_MAX_LENGTH);
const isValidBarcode = (value) => value.length === BARCODE_LENGTH;

const WarehouseBookingInForm = () => {
  const [barcode, setBarcode] = useState("");
  const [locationBarcode, setLocationBarcodeValue] = useState("");
  const [bgColorClass, setBgColorClass] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const firestore = getFirestore();
  const navigate = useNavigate();

  const barcodeInputRef = useRef(null);
  const locationInputRef = useRef(null);

  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);

  const setLocationBarcode = (value) => {
    setLocationBarcodeValue(limitLocation(value));
  };

  const flashRedFeedback = () => {
    setBgColorClass("bg-red-500");
    setTimeout(() => {
      setBgColorClass("");
    }, 1500);
  };

  const flashGreenAfterSubmit = () => {
    setBgColorClass("bg-green-500");
    setTimeout(() => {
      setBgColorClass("");
      barcodeInputRef.current?.focus();
    }, 1500);
  };

  const resetForm = () => {
    setBarcode("");
    setLocationBarcode("");
    barcodeInputRef.current?.focus();
  };

  const handleBarcodeChange = (e) => {
    setBarcode(sanitizeBarcode(e.target.value));
  };

  const handleLocationBarcodeChange = (e) => {
    setLocationBarcode(e.target.value);
  };

  const handleBarcodeKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!isValidBarcode(barcode)) {
      flashRedFeedback();
      return;
    }

    if (barcode === "1234567891234") {
      navigate("/WarehouseBookingInForm");
    } else if (barcode === "1234567891235") {
      navigate("/WarehouseBookingStock");
    } else {
      locationInputRef.current?.focus();
    }
  };

  const handleLocationBarcodeKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    handleSubmit(event);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    const sanitizedBarcode = sanitizeBarcode(barcode);
    const limitedLocation = limitLocation(locationBarcode);
    const locationId = limitedLocation.trim();

    if (sanitizedBarcode !== barcode) {
      setBarcode(sanitizedBarcode);
    }

    if (limitedLocation !== locationBarcode) {
      setLocationBarcode(limitedLocation);
    }

    if (!isValidBarcode(sanitizedBarcode) || !locationId) {
      flashRedFeedback();
      return;
    }

    setIsSubmitting(true);

    const serialNumber = sanitizedBarcode.slice(0, 6);
    const special = sanitizedBarcode.slice(6, 8);
    const productId = sanitizedBarcode.slice(8);
    const isStock = special === "50";

    const productData = {
      ProductId: productId,
      Special: special,
      SerialNumber: serialNumber,
      LocationId: locationId,
      Allocated: false,
      Stock: isStock,
      Dispatch: false,
      currentlyEditingBy: null,
      BookedInTime: Timestamp.now(),
    };

    try {
      await addDoc(collection(firestore, "cylinders_warehouse"), productData);
      console.log("Data added to Firestore.");

      // Update the oldest unscanned instance.
      const barcodeRef = doc(firestore, "factorybarcode", sanitizedBarcode);
      const instancesSnap = await getDocs(collection(barcodeRef, "instances"));

      const unscanned = instancesSnap.docs
        .filter((doc) => !doc.data().hortonwood_scan)
        .sort((a, b) => {
          const aDate = a.data().PrintedAt?.toDate?.() || new Date(0);
          const bDate = b.data().PrintedAt?.toDate?.() || new Date(0);
          return aDate - bDate;
        });

      if (unscanned.length > 0) {
        const oldest = unscanned[0];
        await updateDoc(doc(barcodeRef, "instances", oldest.id), {
          hortonwood_scan: true,
          hortonwood_scantime: Timestamp.now(),
        });
        console.log("Hortonwood scan updated in factorybarcode.");
      } else {
        console.warn("No unscanned instances found for this barcode.");
      }

      resetForm();
      flashGreenAfterSubmit();
    } catch (error) {
      console.error("Error during booking in:", error);
      flashRedFeedback();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FavouriteButton pageUrl={window.location.pathname} />

      {/* Outer container with dynamic background color */}
      <div
        className={`min-h-screen w-full flex flex-col items-center justify-center transition-colors duration-500 ${bgColorClass}`}
      >
        {/* Card Container */}
        <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-md border border-gray-300 mx-4">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">
            Booking In
          </h1>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            {/* Barcode Input */}
            <label
              htmlFor="barcode"
              className="block text-gray-700 font-medium mb-1"
            >
              Scan or Enter Barcode:
            </label>
            <input
              type="text"
              id="barcode"
              name="barcode"
              required
              value={barcode}
              onChange={handleBarcodeChange}
              onKeyDown={handleBarcodeKeyDown}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={13}
              autoComplete="off"
              disabled={isSubmitting}
              ref={barcodeInputRef}
              className="mb-4 w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />

            {/* Location Barcode */}
            <label
              htmlFor="locationBarcode"
              className="block text-gray-700 font-medium mb-1"
            >
              Location:
            </label>
            <input
              type="text"
              id="locationBarcode"
              name="locationBarcode"
              required
              value={locationBarcode}
              onChange={handleLocationBarcodeChange}
              onKeyDown={handleLocationBarcodeKeyDown}
              maxLength={15}
              autoComplete="off"
              disabled={isSubmitting}
              ref={locationInputRef}
              className="mb-4 w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />

            {/* Hidden button so 'Enter' can submit the form */}
            <button type="submit" disabled={isSubmitting} className="hidden">
              Submit
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default WarehouseBookingInForm;
